// internal-deps
import { isNonEmptyString } from "@swiggy-private/payment-core";
import { CheckoutMetaInfoResponsePayload } from "@swiggy-private/native-bridge";
import { format, getAddressInfo, isVersionCodeEligible } from "@swiggy-private/core-ui";
import { CartAddress, CartRestaurantInfo } from "@swiggy-private/payment-ui";
import {
    ANNOTATION_TYPES,
    qFoodRestaurantsList,
    Q_FOOD_SLA,
    MIN_ANDROID_VERSION_Q_FOOD,
    MIN_IOS_VERSION_Q_FOOD,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    DeliveryInfo,
    isEmpty,
} from "@swiggy-private/checkout-core";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// constants
import {
    OTHER_ANNOTATION,
    ICON_MARKER,
    ICON_CIRCLE,
    TEXTS,
    ANNOTATION_ICONS,
    ICON_RESTAURANT,
    PICKUP_ANNOTATION_ICONS,
    ADDRESS_ANNOTATION_TYPES,
    IRCTC_DELIVERY_ANNOTATION,
    ICON_LINE,
} from "@payment-client/constants/DeliveryAddressView";

interface DeliveryAddressViewData {
    pickupName: string;
    pickupSubtext: string;
    PickupIcon: React.FunctionComponent;
    LineIcon: React.FunctionComponent;

    deliveryAddress: string;
    deliveryAnnotation: string;
    DeliveryIcon: React.FunctionComponent;
    deliveryTimeAnnotation?: string;
    deliveryTime?: string;

    slaText?: string;
}

export const getAddressAnnotation = (annotation: string, name: string): string => {
    let addressAnnotation = "";
    switch (annotation.toLowerCase()) {
        case ANNOTATION_TYPES.HOME.toLowerCase():
            addressAnnotation = ADDRESS_ANNOTATION_TYPES.HOME;
            break;
        case ANNOTATION_TYPES.WORK.toLowerCase():
            addressAnnotation = ADDRESS_ANNOTATION_TYPES.WORK;
            break;
        case ANNOTATION_TYPES.FRIENDS_AND_FAMILY.toLowerCase():
            addressAnnotation = name || ADDRESS_ANNOTATION_TYPES.FRIENDS_AND_FAMILY;
            break;
        default:
            addressAnnotation = annotation || OTHER_ANNOTATION;
            break;
    }
    return addressAnnotation;
};

export function getRestaurantName(restaurantDetails: CartRestaurantInfo): string {
    return restaurantDetails.name ?? "";
}

export function getRestaurantId(restaurantDetails: CartRestaurantInfo): string {
    return String(restaurantDetails.id ?? "");
}

export function formatItemsAndSla(itemCount: number | string, slaMessage: string): string {
    const isSlaMessageValid = isNonEmptyString(slaMessage);

    if (itemCount > 1 && isSlaMessageValid) {
        return format(TEXTS.X_ITEMS_Y_ETA, String(itemCount), slaMessage);
    } else if (itemCount === 1 && isSlaMessageValid) {
        return format(TEXTS.ONE_ITEM_X_ETA, slaMessage);
    } else if (itemCount > 1 && !isSlaMessageValid) {
        return format(TEXTS.X_ITEMS, String(itemCount));
    } else if (itemCount === 1 && !isSlaMessageValid) {
        return TEXTS.ONE_ITEM;
    } else if (itemCount < 1 && isSlaMessageValid) {
        return format(TEXTS.X_ETA, slaMessage);
    }
    return "";
}

export function getFormattedSlaMessage(slaTime?: number | string, slaMessage?: string): string {
    if (isNonEmptyString(slaMessage)) {
        return slaMessage;
    }
    if (Number(slaTime) > 0) {
        return `${slaTime} Mins`;
    }
    return "";
}
export function getOrderItemsAndSla(
    cartTotalCount?: number | string,
    slaTime?: number | string,
    slaMessage?: string,
): string {
    const itemCount = Number(cartTotalCount) | 0;
    const formattedSlaMessage = getFormattedSlaMessage(slaTime, slaMessage);
    const text = formatItemsAndSla(itemCount, formattedSlaMessage);
    return text.trim();
}

/** Returns icon for selected address annotation for delivery. */
export const getAddressAnnotationIcon = (annotation?: string | null): React.FunctionComponent => {
    return annotation ? ANNOTATION_ICONS[annotation.toLowerCase()] || ICON_MARKER : ICON_MARKER;
};

export function getCustomerAddress(deliveryAddress?: CartAddress | null): {
    name: string;
    address: string;
    annotation: string;
    Icon: React.FunctionComponent;
} {
    const defaultResponse = {
        address: "",
        annotation: OTHER_ANNOTATION,
        Icon: ICON_MARKER,
        name: "",
    };

    if (!deliveryAddress) return defaultResponse;

    return {
        name: deliveryAddress?.name || "",
        address: getAddressInfo(deliveryAddress),
        Icon: getAddressAnnotationIcon(deliveryAddress.annotation),
        annotation: deliveryAddress.annotation || OTHER_ANNOTATION,
    };
}
export const getPickUpIcon = (annotation?: string): React.FunctionComponent => {
    return annotation ? PICKUP_ANNOTATION_ICONS[annotation.toLowerCase()] || ICON_RESTAURANT : ICON_RESTAURANT;
};

export const getDataForDeliveryAddressView = ({
    checkoutMetaInfo,
    presentationDetails,
}: {
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
    presentationDetails?: GetPaymentMethodsResponse["presentationDetails"];
}): DeliveryAddressViewData | null => {
    if (presentationDetails) {
        const { deliveryAddress, pickUpAddress } = presentationDetails;

        if (!deliveryAddress || !pickUpAddress) return null;

        return {
            pickupName: pickUpAddress.name,
            pickupSubtext: pickUpAddress.address,
            PickupIcon: getPickUpIcon(pickUpAddress.annotation),
            LineIcon: ICON_LINE,

            deliveryAddress: deliveryAddress.address,
            deliveryAnnotation: deliveryAddress.annotation,
            DeliveryIcon: getAddressAnnotationIcon(deliveryAddress.annotation),
        };
    }

    if (checkoutMetaInfo) {
        const { restaurantDetails, deliveryAddress, slaTime, slaMessage, cartItemsCount } = checkoutMetaInfo;

        if (!restaurantDetails) return null;

        const orderDetails = getOrderItemsAndSla(cartItemsCount, slaTime, slaMessage);
        const restaurantName = getRestaurantName(restaurantDetails);
        const { address, annotation, Icon, name } = getCustomerAddress(deliveryAddress);
        return {
            pickupName: restaurantName,
            pickupSubtext: orderDetails,
            PickupIcon: getPickUpIcon(),
            LineIcon: ICON_LINE,

            deliveryAddress: address,
            deliveryAnnotation: getAddressAnnotation(annotation, name),
            DeliveryIcon: Icon,
        };
    }

    return null;
};

export const getQFoodSLA = (checkoutMetaInfo: CheckoutMetaInfoResponsePayload | null | undefined): string => {
    if (checkoutMetaInfo && checkoutMetaInfo.restaurantDetails) {
        const restaurantId = getRestaurantId(checkoutMetaInfo.restaurantDetails);
        const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode) || "";
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        if (
            qFoodRestaurantsList.includes(restaurantId) &&
            isVersionCodeEligible(MIN_ANDROID_VERSION_Q_FOOD, MIN_IOS_VERSION_Q_FOOD, versionCode, platform)
        ) {
            return Q_FOOD_SLA;
        }
    }
    return "";
};

const getPickupSubText = ({
    slaTime,
    slaMessage,
    checkoutMetaInfo,
}: {
    slaTime: number;
    slaMessage?: string;
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
}): string => {
    const formattedSlaMessage = getFormattedSlaMessage(slaTime, slaMessage);
    if (!formattedSlaMessage) {
        return "";
    }
    const qFoodSLA = getQFoodSLA(checkoutMetaInfo);
    const sla = qFoodSLA ? qFoodSLA : formattedSlaMessage.toLowerCase();
    return format(TEXTS.DELIVERY_IN, sla);
};

const getDeliveryTimeForBulkOrder = (deliverySelectedSlot?: DeliveryInfo) => {
    if (deliverySelectedSlot?.deliveryTimeAnnotation && deliverySelectedSlot?.deliveryTime) {
        return `${deliverySelectedSlot?.deliveryTimeAnnotation} | ${deliverySelectedSlot?.deliveryTime}`;
    }
    return "";
};

export const getDataForDeliveryAddressViewV4 = ({
    checkoutMetaInfo,
    presentationDetails,
    deliverySelectedSlot,
}: {
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
    presentationDetails?: GetPaymentMethodsResponse["presentationDetails"];
    deliverySelectedSlot?: DeliveryInfo;
}): DeliveryAddressViewData | null => {
    if (presentationDetails) {
        const { deliveryAddress, pickUpAddress, deliveryTimeAnnotation, deliveryTime } = presentationDetails;

        if (!deliveryAddress || !pickUpAddress) return null;

        return {
            pickupName: pickUpAddress.name,
            pickupSubtext: pickUpAddress.address,
            PickupIcon: ICON_CIRCLE,
            LineIcon: ICON_LINE,
            deliveryAddress: deliveryAddress.address,
            deliveryAnnotation: deliveryAddress.annotation,
            DeliveryIcon: ICON_CIRCLE,
            deliveryTimeAnnotation,
            deliveryTime,
        };
    }

    if (checkoutMetaInfo) {
        const {
            restaurantDetails,
            deliveryAddress,
            slaTime,
            slaMessage,
            cartItemsCount,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            irctc_data,
        } = checkoutMetaInfo;

        if (!restaurantDetails) return null;

        // if checkoutMetaInfo has irctc_data then it is a irctc order
        if (!isEmpty(irctc_data)) {
            const restaurantName = getRestaurantName(restaurantDetails);
            return {
                pickupName: restaurantName,
                pickupSubtext: deliveryAddress?.area || "",
                PickupIcon: ICON_CIRCLE,
                LineIcon: ICON_LINE,
                deliveryAnnotation: format(IRCTC_DELIVERY_ANNOTATION, irctc_data?.coach || "", irctc_data?.berth || ""),
                deliveryAddress: irctc_data?.station_name || "",
                DeliveryIcon: ICON_CIRCLE,
                deliveryTimeAnnotation: "",
                deliveryTime: "",
            };
        }

        // if we get deliverySelectedSlot, it means that this is a bulk order
        if (!isEmpty(deliverySelectedSlot?.deliveryTimeAnnotation) && !isEmpty(deliverySelectedSlot?.deliveryTime)) {
            const restaurantName = getRestaurantName(restaurantDetails);
            const { address, annotation, name } = getCustomerAddress(deliveryAddress);
            return {
                pickupName: restaurantName,
                pickupSubtext: "",
                PickupIcon: ICON_CIRCLE,
                LineIcon: ICON_LINE,
                deliveryAddress: address,
                deliveryAnnotation: getAddressAnnotation(annotation, name),
                DeliveryIcon: ICON_CIRCLE,
                deliveryTimeAnnotation: TEXTS.DELIVERING,
                deliveryTime: getDeliveryTimeForBulkOrder(deliverySelectedSlot),
            };
        }

        if (!slaTime || !cartItemsCount) return null;

        const restaurantName = getRestaurantName(restaurantDetails);
        const { address, annotation, name } = getCustomerAddress(deliveryAddress);
        const pickupSubtext = getPickupSubText({ slaTime, checkoutMetaInfo, slaMessage });
        return {
            pickupName: restaurantName,
            pickupSubtext: pickupSubtext,
            PickupIcon: ICON_CIRCLE,
            LineIcon: ICON_LINE,
            deliveryAddress: address,
            deliveryAnnotation: getAddressAnnotation(annotation, name),
            DeliveryIcon: ICON_CIRCLE,
        };
    }

    return null;
};
